import React, { useEffect } from 'react';
import dayjs from 'dayjs';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { initialState as pendingReleaseEarmarkInitialState } from '~redux/slices/approval';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import * as Styles from './_projectApprovalTableSearchAndFilterBar.module.scss';
import { MetalTypeEnum } from '~utils/enums';
import { TPendingReleaseEarmarkListingFilters } from '~types/feat/approval';

const ReleaseEarmarkApprovalSearchAndFilterBar = () => {
    const filters = useAppSelector(state => state.approval.pendingReleaseEarmarkListing.filters);
    const branchesList = useAppSelector(state => state.branch.allBranches);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);

    const initialFilters = pendingReleaseEarmarkInitialState.pendingReleaseEarmarkListing.filters;

    const dispatch = useAppDispatch();

    const setFilters = (inputFilters: TPendingReleaseEarmarkListingFilters) => dispatch(combinedActions.approvalSetPendingReleaseEarmarkListingFilters(inputFilters));
    const handleSearchClick = () => dispatch(combinedActions.approvalGetPendingReleaseEarmarkListingAttempt());
    const handleCancelClick = () => {
        setFilters({
            ...initialFilters,
            branchCode: !allBranchAccess ? branchCode : '',
            branchName: !allBranchAccess ? (branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '') : '',
        });
        handleSearchClick();
    };

    useEffect(() => {
        if (branchesList.length === 0) return;

        if (allBranchAccess) {
            setFilters({
                ...filters,
                branchName: '',
                branchCode: '',
            });
            return;
        }

        const defaultBranch = branchesList.find(b => b.branchCode === branchCode);

        if (!defaultBranch) return;

        setFilters({
            ...filters,
            branchName: defaultBranch.branchName,
            branchCode: defaultBranch.branchCode,
        });
    }, [branchesList]);

    useEffect(() => {
        return () => {
            setFilters({
                ...initialFilters,
                branchCode: !allBranchAccess ? branchCode : '',
                branchName: !allBranchAccess ? (branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '') : '',
            });
            dispatch(combinedActions.approvalGetPendingReleaseEarmarkListingAttempt());
        };
    }, []);

    const massagedBranchesListOption = () => {
        if (!branchesList.length) return [];

        return branchesList.map((branch) => ({
            label: branch.branchName,
            value: branch.branchCode,
        }));
    };

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Customer Name:',
            type: 'input' as const,
            inputParams: {
                value: filters.customerName,
                setValue: (event) => setFilters({ ...filters, customerName: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'Customer ID No.:',
            type: 'input' as const,
            inputParams: {
                value: filters.customerId,
                setValue: (event) => setFilters({ ...filters, customerId: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchName,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    branchName: event.target.value,
                    branchCode: branchesList.find((branch) => branch.branchName === event.target.value)?.branchCode || '',
                }),
                optionSelected: !!branchesList.find((branch) => branch.branchName === filters.branchName),
                noOptionsMessage: 'No branch found',
                disabled: !allBranchAccess,
            },
        },
        {
            label: 'Metal Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: 'Gold',
                        value: MetalTypeEnum.Gold,
                    },
                    {
                        label: 'Silver',
                        value: MetalTypeEnum.Silver,
                    },
                ],
                value: filters.metalType,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, metalType: event.target.value }),
                optionSelected: !!MetalTypeEnum[filters.metalType as keyof typeof MetalTypeEnum],
                noOptionsMessage: 'No metal type found',
            },
        },
        {
            label: 'Date From:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateFrom,
                setDate: (date: string) => {
                    if (date > filters?.dateTo) {
                        setFilters({ ...filters, dateFrom: date, dateTo: '' });
                    } else setFilters({ ...filters, dateFrom: date });
                },
                maxDate: dayjs().toISOString(),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsStart: true,
            },
        },
        {
            label: 'Date To:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateTo,
                minDate: filters.dateFrom,
                setDate: (date: string) => setFilters({ ...filters, dateTo: date }),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsEnd: true,
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
        />
    );
};

export default ReleaseEarmarkApprovalSearchAndFilterBar;
